/**
 * shoulderCapSeat — the 'cap-height' recentre method (unbind-adjust-rebind
 * tier 2, shoulder instance). Where the Upperarm pivot sits WITHIN the
 * shoulder cap's own vertical extent, and how far it must rise to reach the
 * A-pose band.
 *
 * MEASURED 2026-09-08 (shoulder-seat-probe; the swimsuit set is one prompt
 * whose only variable is the pose keyword, so it is a controlled experiment):
 * a T-posed Tripo body seats the joint LOW in the cap — 0.32–0.69 of the way
 * from its underside to its top — where an A-posed body seats it at
 * 0.64–0.82. The lateral ratio is 0.75–1.02 everywhere: the pivot is not too
 * far OUT, it is too LOW. With the pivot low, the arm below swings out (the
 * underarm gap) and the deltoid above counter-rotates and stays up (the
 * "shoulder pads"). One cause, two symptoms — Allen's field read.
 *
 * THE INSTRUMENT (verbatim from the probe, so the number that proposes the
 * raise is the number the table was built from): the cap is the upper-arm
 * SEGMENT's flesh — vertices whose dominant owner is the Upperarm or a bone
 * hanging from it above the Forearm (the twist supplements included) —
 * within 6 cm of the joint ALONG the bone (3 cm behind it). Its lowest and
 * highest points along world up are the cap's underside and top; the joint's
 * height between them is the fraction.
 *
 * THE RAISE is along world up only, to `targetFraction` of the SAME extent,
 * and never downward: a joint already at or above the band is left alone
 * (raise 0). Inward is a human's call (the recenter's `authoredOffset`);
 * the data does not ask for it — lateral ratio 0.91–0.96 on the three
 * T-posed women.
 *
 * Depends on: math/vec3. Depended on by: the app's shoulder evidence + the
 * recentre door in run-document.
 */
import { KernelError } from '../ir/errors.js';
import { vec3Add, vec3Dot, vec3Length, vec3Normalize, vec3Scale, vec3Sub, type Vec3Data } from '../math/vec3.js';

/**
 * The A-pose band's centre: the five framed swimsuit bodies measured
 * 0.64–0.82 (Pippi 0.82/0.71, Sylvie 0.71/0.67, Suzy 0.75/0.64, Marjorie
 * 0.80/0.78, Greta 0.71/0.68 — median 0.71, midrange 0.73); Allen's ruling
 * (2026-09-08) names ~0.75. A measured band, not a tuned knob.
 */
export const SHOULDER_CAP_TARGET = 0.75;
/** The cap's reach along the bone, in metres (the probe's 6 cm; half of it behind the joint). */
export const SHOULDER_CAP_REACH_M = 0.06;
/** Below this many cap vertices the extent is noise, not a cap. */
export const SHOULDER_CAP_MIN_SAMPLES = 24;

/** A shoulder whose cap cannot be measured says so — never a silent no-op. */
export class ShoulderCapRefusal extends KernelError {
  constructor(readonly reason: string) {
    super(`cap-height recentre: ${reason}`);
  }
}

export interface ShoulderCapInput {
  /** The Upperarm joint, bind-world. */
  readonly joint: Vec3Data;
  /** The Forearm joint (the elbow), bind-world — sets the bone direction. */
  readonly elbow: Vec3Data;
  /** The rig's up axis, bind-world (normalised here). */
  readonly up: Vec3Data;
  /** The upper-arm segment's flesh, bind-world (dominant owner in the segment). */
  readonly armFlesh: readonly Vec3Data[];
  /** World units → metres, so the 6 cm reach scales with the character. */
  readonly metersPerUnit: number;
  /** Where in the cap the joint should sit (default {@link SHOULDER_CAP_TARGET}). */
  readonly targetFraction?: number;
}

export interface ShoulderCapResult {
  /** The joint's height within the cap: 0 = its underside, 1 = its top. */
  readonly fraction: number;
  /** The cap's top above the joint, world units — the "shoulder pad" height. */
  readonly capAboveJoint: number;
  /** The cap's vertical extent, world units. */
  readonly capExtent: number;
  /** Cap vertices the measurement used — evidence, never assumed. */
  readonly sampled: number;
  readonly targetFraction: number;
  /** How far the joint rises, world units, along `up` (≥ 0). */
  readonly raise: number;
  /** Where the joint should sit: `joint + raise·up`. */
  readonly centre: Vec3Data;
}

export function shoulderCapSeat(input: ShoulderCapInput): ShoulderCapResult {
  const target = input.targetFraction ?? SHOULDER_CAP_TARGET;
  if (!(target > 0 && target <= 1)) throw new ShoulderCapRefusal(`target fraction ${target} is outside (0, 1]`);
  if (!(input.metersPerUnit > 0)) throw new ShoulderCapRefusal(`metersPerUnit ${input.metersPerUnit} is not positive`);
  const upLength = vec3Length(input.up);
  if (!(upLength > 0)) throw new ShoulderCapRefusal('the up axis has no length');
  const up = vec3Normalize(input.up);
  const bone = vec3Sub(input.elbow, input.joint);
  if (!(vec3Length(bone) > 0)) throw new ShoulderCapRefusal('the Upperarm and Forearm joints coincide');
  const boneDir = vec3Normalize(bone);
  const reach = SHOULDER_CAP_REACH_M / input.metersPerUnit;
  let lo = Infinity;
  let hi = -Infinity;
  let sampled = 0;
  for (const q of input.armFlesh) {
    const along = vec3Dot(vec3Sub(q, input.joint), boneDir);
    if (along < -reach * 0.5 || along > reach) continue;
    const h = vec3Dot(q, up);
    if (h < lo) lo = h;
    if (h > hi) hi = h;
    sampled += 1;
  }
  if (sampled < SHOULDER_CAP_MIN_SAMPLES) {
    throw new ShoulderCapRefusal(`only ${sampled} upper-arm vertices lie within the cap (need ${SHOULDER_CAP_MIN_SAMPLES})`);
  }
  const extent = hi - lo;
  if (!(extent > 0)) throw new ShoulderCapRefusal('the cap has no vertical extent');
  const jointH = vec3Dot(input.joint, up);
  const fraction = (jointH - lo) / extent;
  const raise = Math.max(0, (target - fraction) * extent);
  return {
    fraction,
    capAboveJoint: hi - jointH,
    capExtent: extent,
    sampled,
    targetFraction: target,
    raise,
    centre: vec3Add(input.joint, vec3Scale(up, raise)),
  };
}
